import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';
import {FmlxDropdown, FmlxTable, FmlxTextBox} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {defaultPlatesIdList} from 'contents/PlateTypeEditor/PlateTypeEditorEnum';

import withIntl from '../../lib/withIntl';
import {checkContainIllegalChars} from '../../lib/stringHelper';
import {isLabwareAlreadyExist, isLabwareNameAlreadyExist} from '../../stores/business/labwareBusinessAction';
import {MappedLabwareAction} from '../../constants/dataEnum';
import {hideSnackbar} from '../../stores/actions/appStateAction';

import FastModal from '../../components/FastModal';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';

import './PlateSelectionModal.scss';

function ImportedPlatesTextBox({
  intl,
  value,
  isDuplicateSameType,
  isDuplicateDifferentType,
  isSelectedLabware,
  onChange,
  maxCharacters,
  onConfimClick,
  isDefaultPlate,
}) {
  const getInlineText = useCallback(() => {
    let inlineText = '';
    if (isDuplicateDifferentType) {
      inlineText = intl.formatMessage({id: 'explorer.modal.plateSelection.text.errorDuplicate'});
    } else if (!value && !isSelectedLabware) {
      inlineText = intl.formatMessage({id: 'explorer.modal.plateSelection.text.errorBlankName'});
    } else if (isDuplicateSameType && isDefaultPlate) {
      inlineText = intl.formatMessage({id: 'explorer.modal.plateSelection.text.warningDuplicateIsDefault'});
    } else if (isDuplicateSameType) {
      inlineText = intl.formatMessage({id: 'explorer.modal.plateSelection.text.warningDuplicate'});
    }

    return inlineText;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectedLabware, value, isDuplicateDifferentType, isDuplicateSameType, isDefaultPlate]);

  const onEnterHandler = () => {
    onConfimClick();
  };

  const getClassName = () => {
    if (isDuplicateSameType) {
      return 'warning';
    }
    return '';
  };

  return (
    <div className={`plate-selection-modal__text-box ${getClassName()}`}>
      <FmlxTextBox
        value={value}
        onChange={onChange}
        inlineText={getInlineText()}
        error={isDuplicateDifferentType || isDuplicateSameType || (!value && !isSelectedLabware)}
        disabled={isSelectedLabware}
        maxLength={maxCharacters}
        onEnterKeyPressed={onEnterHandler}
      />
    </div>
  );
}

ImportedPlatesTextBox.propTypes = {
  intl: PropTypes.any.isRequired,
  value: PropTypes.string.isRequired,
  isDuplicateSameType: PropTypes.bool.isRequired,
  isDuplicateDifferentType: PropTypes.bool.isRequired,
  isSelectedLabware: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onConfimClick: PropTypes.func.isRequired,
  maxCharacters: PropTypes.number.isRequired,
  isDefaultPlate: PropTypes.bool.isRequired,
};

function PlateSelectionModal({showModal, onCancelClick, onConfimClick, data, maxCharacters, intl}) {
  // redux state
  const availableLabwareState = useSelector((state) => state.availableLabwareState);
  const defaultPlateNames = Object.values(availableLabwareState)
    .filter((ele) => defaultPlatesIdList.includes(ele.LabwareID))
    .map((ele) => ele.name);

  // dispatch
  const dispatch = useDispatch();
  const labwareBusinessActionIsLabwareNameAlreadyExist = bindActionCreators(isLabwareNameAlreadyExist, dispatch);
  const labwareBusinessActionIsLabwareAlreadyExist = bindActionCreators(isLabwareAlreadyExist, dispatch);
  const appStateActionHideSnackbar = bindActionCreators(hideSnackbar, dispatch);
  // state
  const [labwareMapping, setLabwareMapping] = useState([]);

  const generateData = (labware, labwareName = null, selectedLabware = null) => {
    const filteredLabware = Object.values(availableLabwareState)
      .filter(
        (e) =>
          e.rowCount === labware.rowCount && e.colCount === labware.colCount && e.isSpearBio === labware.isSpearBio,
      )
      .sort((a, b) => a.name?.localeCompare(b.name));
    const isSelectedLabwareExist = filteredLabware.find((e) => e.LabwareID === selectedLabware?.LabwareID);

    let computedSelectedLabware = null;
    let isDuplicateSameType = false;
    let isDuplicateDifferentType = false;

    if (selectedLabware) {
      if (isSelectedLabwareExist) {
        computedSelectedLabware = selectedLabware;
      }
    } else if (filteredLabware.length) {
      [computedSelectedLabware = null] = filteredLabware.filter((e) => e.name === labware.name);
    }

    /**
     * This will check if no plate is selected.
     * And inform user that plate with same type and name or different type and same, already exist/not exist in DB.
     */
    if (!computedSelectedLabware) {
      isDuplicateSameType = labwareBusinessActionIsLabwareNameAlreadyExist(
        labwareName ?? labware.name,
        null,
        true,
        labware,
      );
      isDuplicateDifferentType = labwareBusinessActionIsLabwareNameAlreadyExist(
        labwareName ?? labware.name,
        null,
        false,
        labware,
      );
    }

    return {
      _original: labware,
      labwareId: labware.LabwareID,
      labwareName: labwareName ?? labware.name,
      selectedLabware: computedSelectedLabware,
      isDuplicateSameType,
      isDuplicateDifferentType,
      isDefaultPlate: defaultPlateNames.includes(labwareName ?? labware.name),
      existingLabwareList: filteredLabware.map((e, idx) => ({
        _original: e,
        id: idx + 1, // index 0 is reserved for using imported plate
        text: e.name,
      })),
      show: !(labware.isExist ?? labwareBusinessActionIsLabwareAlreadyExist(labware)), // only show labware with different hash
    };
  };

  const generateLabwareMapping = () => {
    const mappedData = data.map((labware) => generateData(labware));

    setLabwareMapping(mappedData);
  };

  const regenerateLabwareMapping = () => {
    const mappedData = labwareMapping.map((labware) =>
      generateData(labware._original, labware.labwareName, labware.selectedLabware),
    );

    setLabwareMapping(mappedData);
  };

  const onSelectLabwareList = (item, plateMappedIndex) => {
    const newData = [...labwareMapping];
    const labwareData = newData[plateMappedIndex];

    if (item.LabwareID === -1) {
      newData[plateMappedIndex] = generateData(labwareData._original, labwareData.labwareName, item);
    } else {
      const selectedLabware = item._original;

      newData[plateMappedIndex] = {
        ...labwareData,
        selectedLabware,
        isDuplicateSameType: false,
        isDuplicateDifferentType: false,
        isDefaultPlate: false,
      };
    }

    setLabwareMapping(newData);
  };

  const onChangeLabwareName = (obj, plateMappedIndex) => {
    const {value: newValue, maxLength} = obj;

    if (newValue.length > maxLength || checkContainIllegalChars(newValue)) {
      return;
    }

    const newData = [...labwareMapping];
    const labwareData = newData[plateMappedIndex];
    const originalLabwareData = labwareData._original;

    newData[plateMappedIndex] = {
      ...labwareData,
      labwareName: newValue,
      isDuplicateSameType: labwareBusinessActionIsLabwareNameAlreadyExist(newValue, null, true, originalLabwareData),
      isDuplicateDifferentType: labwareBusinessActionIsLabwareNameAlreadyExist(
        newValue,
        null,
        false,
        originalLabwareData,
      ),
      isDefaultPlate: defaultPlateNames.includes(newValue),
    };

    setLabwareMapping(newData);
  };

  const isLabwareValid = () => {
    const isLabwareTypeValid = labwareMapping.map((e) => !e.isDuplicateDifferentType);
    const isLabwareNameValid = labwareMapping.map(
      (e) => (e.labwareName.length > 0 && !e.selectedLabware) || Boolean(e.selectedLabware),
    );

    const isNotDefaultPlate = labwareMapping.map((e) => !e.isDefaultPlate);

    const isValid =
      !isLabwareTypeValid.includes(false) && !isLabwareNameValid.includes(false) && isNotDefaultPlate.includes(true);

    return isValid;
  };

  const handleConfirmClick = () => {
    appStateActionHideSnackbar();

    const mappedLabwareIds = labwareMapping.map((lab) => {
      const {
        isDuplicateSameType,
        selectedLabware,
        labwareName,
        _original: importedLabware,
        show,
        existingLabwareList,
      } = lab;
      let action;
      let newLabwareName;
      let labwareId;

      if (!show) {
        // imported labware's hash is the same as existing labware which not displayed in the pop up modal
        const existingLabware = Object.values(availableLabwareState).find((e) => e.hash === importedLabware.hash);
        action = MappedLabwareAction.USE_EXISTING;
        newLabwareName = importedLabware.name;
        labwareId = existingLabware.LabwareID;
      } else if (selectedLabware) {
        // user select an existing labware
        action = MappedLabwareAction.USE_EXISTING;
        newLabwareName = selectedLabware.name;
        labwareId = selectedLabware.LabwareID;
      } else if (!selectedLabware && !isDuplicateSameType) {
        // user select imported labware
        action = MappedLabwareAction.USE_IMPORTED;
        newLabwareName = labwareName;
        labwareId = importedLabware.LabwareID;
      } else if (!selectedLabware && isDuplicateSameType) {
        // user select imported labware but imported labware name is the same as existing labware
        const existingLabware = existingLabwareList.find(
          (labware) => labware._original.name.toLowerCase() === labwareName.toLowerCase(),
        )?._original;

        action = MappedLabwareAction.USE_REPLACE;
        newLabwareName = labwareName;
        labwareId = existingLabware?.LabwareID;
      }
      return {Action: action, ID: labwareId, NewName: newLabwareName, Hash: importedLabware.hash};
    });

    onConfimClick(mappedLabwareIds);
  };

  const handleCancelButton = () => {
    appStateActionHideSnackbar();
    onCancelClick();
  };

  const generateTableData = (labwareData) => {
    const filterelabwareData = [...labwareData];

    const tableData = filterelabwareData
      .map((e, index) => {
        const dropDownItems = [
          {
            id: 0,
            LabwareID: -1,
            text: intl.formatMessage({id: 'explorer.modal.plateSelection.dropDown.item'}),
            _original: {},
          },
          ...e.existingLabwareList,
        ];

        const selectedItem = dropDownItems.findIndex(
          (item) => item._original.LabwareID && item._original.LabwareID === e.selectedLabware?.LabwareID,
        );

        /**
         * Following code is commented because it causes issue if labware data have duplicate plates in list.
         * which can happen if we try to import more then one protocol at same time and both uses same plate.
         * In that case we need to show both plates in the list and user can update plate selection for any of them.
         * But below code will only fetch index from first plate and will not reach to second plate.
         * Related OP is: https://openproject.formulatrix.com/projects/fast/work_packages/68741/activity
         */
        // const originalLabwareIndex = labwareData.findIndex((lab) => lab.labwareId === e.labwareId);

        return {
          importedPlates: (
            <ImportedPlatesTextBox
              intl={intl}
              value={e.labwareName}
              isDuplicateSameType={e.isDuplicateSameType}
              isDuplicateDifferentType={e.isDuplicateDifferentType}
              isSelectedLabware={Boolean(e.selectedLabware)}
              onChange={(obj) => onChangeLabwareName(obj, index)}
              onConfimClick={onConfimClick}
              maxCharacters={maxCharacters}
              isDefaultPlate={e.isDefaultPlate}
            />
          ),
          plateSelection: (
            <FmlxDropdown
              items={dropDownItems}
              selectedItem={selectedItem === -1 ? 0 : selectedItem}
              onChange={(item) => onSelectLabwareList(dropDownItems[item], index)}
            />
          ),
          show: e.show,
        };
      })
      .filter((e) => e.show);

    return tableData;
  };

  useEffect(() => {
    generateLabwareMapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (labwareMapping.length) {
      regenerateLabwareMapping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLabwareState]);

  const COLUMNS = useMemo(
    () => [
      {
        Header: 'Imported Plates',
        accessor: 'importedPlates',
        disableSort: true,
        hideSort: true,
        hideFilter: true,
        headerStyle: {width: '55%', fontWeight: 'bold'},
      },
      {
        Header: 'Plate Selection',
        accessor: 'plateSelection',
        disableSort: true,
        hideSort: true,
        hideFilter: true,
        headerStyle: {width: '45%', fontWeight: 'bold'},
      },
    ],
    [],
  );

  // useEffect(() => {
  //   const handleKeyDown = (event) => {
  //     if (event.key === 'Enter' && labwareMapping.length > 0) {
  //       if (isLabwareValid()) {
  //         handleConfirmClick();
  //       }
  //     }
  //   };

  //   if (labwareMapping.length > 0) {
  //     document.addEventListener('keydown', handleKeyDown);
  //   }

  //   return () => {
  //     if (labwareMapping.length > 0) {
  //       document.removeEventListener('keydown', handleKeyDown);
  //     }
  //   };
  // }, [labwareMapping]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedDataSource = useMemo(() => generateTableData(labwareMapping), [labwareMapping]);

  return (
    <FastModal
      show={showModal}
      btnAction={[
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            handleConfirmClick();
          },
          text: intl.formatMessage({id: 'explorer.modal.plateSelection.firstButton'}),
          isValid: isLabwareValid(),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            handleCancelButton();
          },
          text: intl.formatMessage({id: 'explorer.modal.plateSelection.secondButton'}),
        },
      ]}
      header={intl.formatMessage({id: 'explorer.modal.plateSelection.title'})}
      size="xl"
      closeDisabled
      content={
        <>
          <div style={{marginBottom: 16}}>
            <FormattedHTMLMessage id="explorer.modal.plateSelection.text" />
          </div>
          <FmlxTable
            data={memoizedDataSource}
            columns={COLUMNS}
            activateSingleSelect={false}
            allowSelectedRow={false}
          />
        </>
      }
      onClose={onCancelClick}
    />
  );
}

PlateSelectionModal.propTypes = {
  intl: PropTypes.any,
  showModal: PropTypes.bool,
  onCancelClick: PropTypes.func,
  onConfimClick: PropTypes.func,
  data: PropTypes.array,
  maxCharacters: PropTypes.number,
};

PlateSelectionModal.defaultProps = {
  intl: {},
  showModal: true,
  onCancelClick: () => {},
  onConfimClick: () => {},
  maxCharacters: 9999,
  data: [],
};

export default withIntl(PlateSelectionModal);
